import { Cloud, GitFork, ImageIcon, Loader2, Paperclip, Plus, Send, Shield, Square, Terminal, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { BudgetBanner } from "@/components/chat/BudgetBanner";
import { ContextDetailPanel } from "@/components/chat/ContextDetailPanel";
import { VoiceInputButton } from "@/components/shared/VoiceInputButton";
import { getRuntimeConfig } from "@/config/runtime";
import { ALLOWED_IMAGE_TYPES, CHAT_ATTACHMENT_ACCEPT_STRING, isLargeTextAttachment } from "@/constants/files";
import { useAuth } from "@/context/AuthContext";
import { useVoiceDictation } from "@/hooks/useVoiceDictation";
import { formatCost, formatTokens } from "@/lib/formatters";
import type { ChatMessage, SharedSessionInfo, SharePointItem, SlashCommand } from "@/types";
import { detectSlashCommand, handleSlashCommandKeyDown } from "./slashCommands";

/**
 * Props for the ChatInput component.
 *
 * Rather than importing the full hook return types we accept the minimal
 * slice of each hook that the input area actually needs.  This keeps the
 * component loosely coupled to the hook implementations.
 */
interface ChatInputProps {
  /** Attachment state + handlers. */
  attachments: {
    uploadedFiles: File[];
    sharepointFiles: SharePointItem[];
    isResolvingUrl: boolean;
    isDraggingOver: boolean;
    isDownloadingFiles: boolean;
    isUploadingImages: boolean;
    handleFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleImageUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleDragOver: (e: React.DragEvent) => void;
    handleDragLeave: (e: React.DragEvent) => void;
    handleDrop: (e: React.DragEvent) => void;
    handlePaste: (e: React.ClipboardEvent) => void;
    removeFile: (index: number) => void;
    setSharepointFiles: React.Dispatch<React.SetStateAction<SharePointItem[]>>;
    setIsSharePointBrowserOpen: (v: boolean) => void;
  };
  /** Streaming state + send handler. */
  streaming: {
    isStreaming: boolean;
    /** A routed first message waits for the user to pick an agent (MHS-627). */
    routePending?: boolean;
    handleSend: () => void;
    disconnectStream: () => void;
    contextInfo?: import("@/hooks/useChatStreaming").ContextInfo | null;
  };
  /** Session state used by the input. */
  session: {
    inputValue: string;
    setInputValue: React.Dispatch<React.SetStateAction<string>>;
    modelSupportsVision: boolean;
    /** User-sent messages for up/down arrow history recall. */
    sentMessages?: string[];
    /** Slash commands from the selected agent (if any). */
    slashCommands?: SlashCommand[];
    /** All messages in the current session (for token usage display). */
    messages?: ChatMessage[];
  };
  /** Sharing state used by the input. */
  chatSharing: {
    sharedInfo: SharedSessionInfo | null;
    isForking: boolean;
  };
}

// ── Message history navigation (terminal-style up/down arrow recall) ────────

function navigateHistoryUp(
  textarea: HTMLTextAreaElement | null,
  sentMessages: string[],
  historyIndexRef: React.MutableRefObject<number>,
  draftRef: React.MutableRefObject<string>,
  inputValue: string,
  setInputValue: React.Dispatch<React.SetStateAction<string>>,
): boolean {
  if (sentMessages.length === 0) return false;
  if (textarea?.selectionStart !== 0 || textarea?.selectionEnd !== 0) return false;
  if (historyIndexRef.current === -1) {
    draftRef.current = inputValue;
  }
  const nextIndex = Math.min(historyIndexRef.current + 1, sentMessages.length - 1);
  historyIndexRef.current = nextIndex;
  setInputValue(sentMessages[sentMessages.length - 1 - nextIndex]);
  return true;
}

function navigateHistoryDown(
  historyIndexRef: React.MutableRefObject<number>,
  draftRef: React.MutableRefObject<string>,
  sentMessages: string[],
  setInputValue: React.Dispatch<React.SetStateAction<string>>,
): boolean {
  if (historyIndexRef.current < 0) return false;
  const nextIndex = historyIndexRef.current - 1;
  if (nextIndex < 0) {
    historyIndexRef.current = -1;
    setInputValue(draftRef.current);
  } else {
    historyIndexRef.current = nextIndex;
    setInputValue(sentMessages[sentMessages.length - 1 - nextIndex]);
  }
  return true;
}

/**
 * Chat input area — file previews, upload buttons, textarea, send button.
 * Extracted from Chat.tsx to reduce the monolith.
 */
export function ChatInput({ attachments, streaming, session, chatSharing }: Readonly<ChatInputProps>) {
  const { profile } = useAuth();
  const showContextInfo = profile?.preferences?.showContextInfo ?? false;
  const isBusy =
    streaming.isStreaming ||
    chatSharing.isForking ||
    attachments.isDownloadingFiles ||
    attachments.isUploadingImages ||
    !!streaming.routePending;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  // Live "fill as you speak" voice dictation → chat input.
  const voiceDictation = useVoiceDictation(session.setInputValue);

  // Close menu when clicking outside
  useEffect(() => {
    if (!isMenuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isMenuOpen]);

  // Auto-grow textarea
  const adjustHeight = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    // Min 1 row (~40px), max ~6 rows (~168px)
    el.style.height = `${Math.min(el.scrollHeight, 168)}px`;
  }, []);

  useEffect(() => {
    adjustHeight();
  }, [session.inputValue, adjustHeight]);

  // Message history navigation (up/down arrow keys like terminal)
  const historyIndexRef = useRef(-1);
  const draftRef = useRef("");
  const sentMessages = session.sentMessages || [];

  // ── Slash command autocomplete ─────────────────────────────────────────
  const slashCommands = session.slashCommands || [];
  const [slashQuery, setSlashQuery] = useState<string | null>(null);
  const [slashIndex, setSlashIndex] = useState(0);
  const slashBlurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const filteredSlashCommands = useMemo(
    () =>
      slashQuery !== null ? slashCommands.filter((c) => c.name.toLowerCase().startsWith(slashQuery.toLowerCase())) : [],
    [slashQuery, slashCommands],
  );

  // Memoize token/cost totals from session messages
  const sessionUsage = useMemo(() => {
    const messages = session.messages;
    if (!messages || messages.length === 0) return null;

    const totalCost = messages.reduce((sum, m) => sum + ((m.messageMetadata?.cost_usd as number) || 0), 0);
    const totalTokens = messages.reduce((sum, m) => sum + (m.tokensUsed || m.tokenCount || 0), 0);
    if (totalTokens === 0 && totalCost === 0) return null;

    const costStr = totalCost > 0 ? formatCost(totalCost) : null;
    const tokenStr = `${formatTokens(totalTokens)} tokens`;
    return { costStr, tokenStr };
  }, [session.messages]);

  // Reset highlight when query changes
  useEffect(() => {
    setSlashIndex(0);
  }, [slashQuery]);

  const closeSlashDropdown = useCallback(() => {
    if (slashBlurTimerRef.current) {
      clearTimeout(slashBlurTimerRef.current);
      slashBlurTimerRef.current = null;
    }
    setSlashQuery(null);
  }, []);

  // Clean up blur timer on unmount
  useEffect(() => {
    return () => {
      if (slashBlurTimerRef.current) clearTimeout(slashBlurTimerRef.current);
    };
  }, []);

  const insertSlashCommand = useCallback(
    (command: SlashCommand) => {
      const newValue = `/${command.name} `;
      session.setInputValue(newValue);
      closeSlashDropdown();
      // Reposition cursor after the trailing space
      const cursorPos = newValue.length;
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.selectionStart = cursorPos;
          textareaRef.current.selectionEnd = cursorPos;
          textareaRef.current.focus();
        }
      }, 0);
    },
    [session.setInputValue, closeSlashDropdown],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      const cursorPos = e.target.selectionStart ?? 0;
      session.setInputValue(value);

      // Detect slash command context
      if (slashCommands.length > 0) {
        const slash = detectSlashCommand(value, cursorPos);
        if (slash) {
          setSlashQuery(slash.query);
          return;
        }
      }
      closeSlashDropdown();
    },
    [session.setInputValue, slashCommands.length, closeSlashDropdown],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Slash command keyboard navigation takes priority when dropdown is open
    if (slashQuery !== null && filteredSlashCommands.length > 0) {
      const consumed = handleSlashCommandKeyDown(
        e,
        filteredSlashCommands,
        slashIndex,
        setSlashIndex,
        insertSlashCommand,
        closeSlashDropdown,
      );
      if (consumed) return;
    }

    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      historyIndexRef.current = -1;
      draftRef.current = "";
      streaming.handleSend();
      return;
    }

    if (e.key === "ArrowUp") {
      const handled = navigateHistoryUp(
        textareaRef.current,
        sentMessages,
        historyIndexRef,
        draftRef,
        session.inputValue,
        session.setInputValue,
      );
      if (handled) e.preventDefault();
      return;
    }

    if (e.key === "ArrowDown") {
      const handled = navigateHistoryDown(historyIndexRef, draftRef, sentMessages, session.setInputValue);
      if (handled) e.preventDefault();
      return;
    }

    if (e.key === "Escape" && historyIndexRef.current >= 0) {
      historyIndexRef.current = -1;
      session.setInputValue(draftRef.current);
    }
  };

  // Read-only shared chats show a non-interactive placeholder
  if (chatSharing.sharedInfo?.permission === "read") {
    return (
      <div
        className={`border-t p-4 bg-surface-primary transition ${
          attachments.isDraggingOver ? "border-red-500/50 border-dashed" : "border-border-primary"
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          attachments.handleDragOver(e);
        }}
        onDragLeave={attachments.handleDragLeave}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          // Don't process the drop — just clear the drag state
          attachments.handleDragLeave(e as unknown as React.DragEvent);
        }}
      >
        {attachments.isDraggingOver && (
          <div className="flex items-center justify-center gap-2 py-3 mb-3 bg-red-500/10 border border-red-500/30 rounded-lg">
            <Shield className="w-5 h-5 text-red-400" />
            <span className="text-sm text-red-400 font-medium">Cannot drop files in a read-only chat</span>
          </div>
        )}
        <div className="flex items-center justify-center gap-2 py-3 text-content-quaternary text-sm">
          <Shield className="w-4 h-4" />
          <span>This is a read-only shared chat. You cannot send messages.</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`border-t p-4 bg-surface-primary transition ${
        attachments.isDraggingOver ? "border-brand-primary border-dashed" : "border-border-primary"
      }`}
      onDragOver={attachments.handleDragOver}
      onDragLeave={attachments.handleDragLeave}
      onDrop={attachments.handleDrop}
    >
      <div className="max-w-4xl mx-auto">
        {/* Budget status banner */}
        <BudgetBanner />
        {/* Drop zone overlay */}
        {attachments.isDraggingOver && (
          <div className="flex items-center justify-center gap-2 py-3 mb-3 bg-brand-primary-subtle border border-brand-primary/30 rounded-lg">
            <ImageIcon className="w-5 h-5 text-brand-primary" />
            <span className="text-sm text-brand-primary font-medium">Drop files here</span>
          </div>
        )}
        {/* Uploaded file previews */}
        {attachments.uploadedFiles.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {attachments.uploadedFiles.map((file, index) => {
              const isImage = ALLOWED_IMAGE_TYPES.has(file.type);
              return (
                <div key={`upload-${file.name}-${index}`} className="relative group">
                  {isImage ? (
                    <img
                      src={URL.createObjectURL(file)}
                      alt={file.name}
                      className="w-16 h-16 rounded-lg object-cover border border-border-secondary"
                    />
                  ) : (
                    <div className="flex items-center gap-2 bg-surface-secondary pl-3 pr-2 py-2 rounded-lg border border-border-secondary">
                      <Paperclip className="w-3.5 h-3.5 text-content-tertiary shrink-0" />
                      <span className="text-sm text-content-secondary truncate max-w-[200px]">{file.name}</span>
                    </div>
                  )}
                  <button
                    onClick={() => attachments.removeFile(index)}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-surface-tertiary hover:bg-red-500 rounded-full flex items-center justify-center transition opacity-0 group-hover:opacity-100"
                  >
                    <X className="w-3 h-3 text-white" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
        {attachments.uploadedFiles.some(isLargeTextAttachment) && (
          <p role="note" className="text-xs text-content-tertiary mb-3">
            Large file attached: the AI may only read the first part of{" "}
            {attachments.uploadedFiles
              .filter(isLargeTextAttachment)
              .map((f) => f.name)
              .join(", ")}
            . For full analysis, use an agent with code execution.
          </p>
        )}

        {/* SharePoint file chips */}
        {(attachments.sharepointFiles.length > 0 || attachments.isResolvingUrl) && (
          <div className="flex flex-wrap gap-2 mb-3">
            {attachments.sharepointFiles.map((file, index) => (
              <div
                key={file.id}
                className="flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 pl-3 pr-2 py-2 rounded-lg"
              >
                <Cloud className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                <span className="text-sm text-content-secondary truncate max-w-[200px]">{file.name}</span>
                <button
                  onClick={() =>
                    attachments.setSharepointFiles(
                      (prev) => prev.filter((_, i) => i !== index), // NOSONAR — unused _ param; only index needed for positional removal
                    )
                  }
                  className="p-1 hover:bg-interactive-active rounded-sm transition"
                >
                  <X className="w-3 h-3 text-content-tertiary" />
                </button>
              </div>
            ))}
            {attachments.isResolvingUrl && (
              <div className="flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 pl-3 pr-3 py-2 rounded-lg">
                <Loader2 className="w-3.5 h-3.5 text-blue-400 animate-spin shrink-0" />
                <span className="text-sm text-content-tertiary">Resolving link...</span>
              </div>
            )}
          </div>
        )}

        {/* Input row — attachment menu, textarea, send/stop */}
        <div className="flex items-end gap-2">
          {/* Attachment overflow menu */}
          <div className="relative shrink-0" ref={menuRef}>
            <button
              onClick={() => setIsMenuOpen((prev) => !prev)}
              disabled={isBusy}
              className={`flex items-center justify-center w-10 h-10 rounded-lg transition border shrink-0 ${
                isMenuOpen
                  ? "bg-brand-primary-subtle border-brand-primary/30 text-brand-primary"
                  : "bg-surface-secondary hover:bg-interactive-active border-border-secondary text-content-tertiary hover:text-content-primary"
              } disabled:opacity-50 disabled:cursor-not-allowed`}
              title="Attach files"
            >
              <Plus className={`w-5 h-5 transition-transform ${isMenuOpen ? "rotate-45" : ""}`} />
            </button>
            {isMenuOpen && (
              <div className="absolute bottom-12 left-0 z-50 bg-surface-secondary border border-border-secondary rounded-lg shadow-xl py-1 min-w-[200px]">
                <label className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-content-primary hover:bg-interactive-active transition cursor-pointer">
                  <Paperclip className="w-4 h-4 text-content-tertiary" />
                  Upload files
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={CHAT_ATTACHMENT_ACCEPT_STRING}
                    onChange={(e) => {
                      attachments.handleFileUpload(e);
                      setIsMenuOpen(false);
                    }}
                    className="hidden"
                  />
                </label>
                <label
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm transition ${
                    session.modelSupportsVision
                      ? "text-content-primary hover:bg-interactive-active cursor-pointer"
                      : "text-content-quaternary cursor-not-allowed opacity-50"
                  }`}
                  title={
                    session.modelSupportsVision
                      ? "Upload images for vision analysis"
                      : "Selected model does not support image input"
                  }
                >
                  <ImageIcon className="w-4 h-4 text-content-tertiary" />
                  Upload images
                  <input
                    ref={imageInputRef}
                    type="file"
                    multiple
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={(e) => {
                      attachments.handleImageUpload(e);
                      setIsMenuOpen(false);
                    }}
                    disabled={!session.modelSupportsVision}
                    className="hidden"
                  />
                </label>
                <button
                  onClick={() => {
                    attachments.setIsSharePointBrowserOpen(true);
                    setIsMenuOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-content-primary hover:bg-interactive-active transition"
                >
                  <Cloud className="w-4 h-4 text-content-tertiary" />
                  {getRuntimeConfig().authProvider === "google" ? "Add from Drive" : "Add from SharePoint"}
                </button>
              </div>
            )}
          </div>

          {/* Voice input button with label */}
          <div className="shrink-0">
            <VoiceInputButton dictation={voiceDictation} disabled={isBusy} label="Voice" />
          </div>

          {/* Textarea with slash command dropdown */}
          <div className="relative flex-1 flex flex-col">
            {/* Slash command autocomplete dropdown */}
            {slashQuery !== null && filteredSlashCommands.length > 0 && (
              <div className="absolute bottom-full left-0 mb-2 w-96 bg-surface-secondary border border-border-secondary rounded-lg shadow-xl overflow-hidden z-50">
                <div className="px-3 py-1.5 text-xs text-content-quaternary border-b border-border-secondary flex items-center gap-1.5">
                  <Terminal className="w-3 h-3" />
                  Agent commands
                </div>
                {filteredSlashCommands.map((command, idx) => (
                  <button
                    key={command.name}
                    onClick={() => insertSlashCommand(command)}
                    onMouseEnter={() => setSlashIndex(idx)}
                    className={`w-full flex items-start gap-3 px-3 py-2 text-left transition ${
                      idx === slashIndex
                        ? "bg-surface-tertiary text-content-primary"
                        : "text-content-secondary hover:bg-surface-tertiary/50"
                    }`}
                  >
                    <span className="text-sm font-mono font-medium text-brand-primary shrink-0">/{command.name}</span>
                    {command.description && (
                      <span className="text-xs text-content-quaternary pt-0.5">{command.description}</span>
                    )}
                  </button>
                ))}
              </div>
            )}
            <textarea
              ref={textareaRef}
              value={session.inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onPaste={attachments.handlePaste}
              onBlur={() => {
                // Delay close so click on dropdown item registers first
                slashBlurTimerRef.current = setTimeout(closeSlashDropdown, 200);
              }}
              placeholder={inputPlaceholder(
                !!streaming.routePending,
                chatSharing.sharedInfo?.permission === "read_write",
                slashCommands.length > 0,
              )}
              disabled={isBusy}
              className="w-full bg-surface-secondary text-content-primary px-4 py-2.5 rounded-lg border border-border-secondary focus:outline-hidden focus:ring-2 focus:ring-brand-primary placeholder-content-quaternary disabled:opacity-50 resize-none overflow-y-auto"
              rows={1}
              style={{ minHeight: "40px", maxHeight: "168px" }}
            />
          </div>

          <div className="shrink-0">
            {streaming.isStreaming ? (
              <button
                onClick={streaming.disconnectStream}
                className="flex items-center justify-center w-10 h-10 bg-red-500/80 hover:bg-red-500 rounded-lg transition"
                title="Stop generating"
              >
                <Square className="w-4 h-4 text-white fill-white" />
              </button>
            ) : (
              <button
                onClick={streaming.handleSend}
                disabled={!session.inputValue.trim() || isBusy}
                className="flex items-center justify-center w-10 h-10 bg-brand-gradient hover:opacity-90 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {(() => {
                  if (chatSharing.sharedInfo?.permission === "read_write")
                    return <GitFork className="w-5 h-5 text-white" />;
                  return <Send className="w-5 h-5 text-white" />;
                })()}
              </button>
            )}
          </div>
        </div>

        {/* Context usage panel (user preference) */}
        {showContextInfo && streaming.contextInfo && <ContextDetailPanel info={streaming.contextInfo} />}

        <div className="mt-1.5 flex items-center justify-between text-xs text-content-quaternary">
          <span>Press Enter to send, Shift+Enter for new line</span>
          {session.messages && session.messages.length > 0 && sessionUsage && (
            <span>
              {sessionUsage.costStr ? `${sessionUsage.costStr} · ` : ""}
              {sessionUsage.tokenStr} ·{" "}
              <Link to="/settings?tab=usage" className="hover:text-content-tertiary underline">
                View usage
              </Link>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function inputPlaceholder(routePending: boolean, forksChat: boolean, hasSlashCommands: boolean): string {
  if (routePending) return "Choose an agent above, or continue without one";
  if (forksChat) return "Type a message to fork this chat...";
  return hasSlashCommands ? "Type your message... (/ for commands)" : "Type your message...";
}
